// Field groups for the theme picker.
//
// Mirrors the columns on `company_theme` (server migration 014) grouped by
// the categories in COLOUR_SCHEME.md. Each entry's `label` is what the
// picker UI renders next to the swatch; `key` is the database column name
// (and the CSS custom property derives from it: color_primary →
// --color-primary).

export interface ThemeField {
  key: string;
  label: string;
  defaultValue: string;
}

export interface ThemeFieldGroup {
  title: string;
  description?: string;
  fields: ThemeField[];
}

export const THEME_GROUPS: ThemeFieldGroup[] = [
  {
    title: 'Primary colours',
    description: 'Brand colours used for primary actions and accents.',
    fields: [
      { key: 'color_primary', label: 'Primary', defaultValue: '#0f172a' },
      { key: 'color_primary_hover', label: 'Primary hover', defaultValue: '#1e293b' },
      { key: 'color_primary_light', label: 'Primary light', defaultValue: '#162236' },
      { key: 'color_secondary', label: 'Secondary', defaultValue: '#2563eb' },
    ],
  },
  {
    title: 'Background colours',
    description: 'Page surfaces, cards, navigation, subheader, and table backgrounds.',
    fields: [
      { key: 'color_page_bg', label: 'Page background', defaultValue: '#f8fafc' },
      { key: 'color_card_bg', label: 'Card background', defaultValue: '#ffffff' },
      { key: 'color_subheader_bg', label: 'Subheader background', defaultValue: '#ffffff' },
      { key: 'color_nav_bg', label: 'Nav background', defaultValue: '#0f172a' },
      { key: 'color_nav_active_bg', label: 'Nav active bg', defaultValue: '#162236' },
      { key: 'color_nav_hover_bg', label: 'Nav hover bg', defaultValue: '#1e293b' },
      { key: 'color_table_header_bg', label: 'Table header bg', defaultValue: '#f8fafc' },
      { key: 'color_table_row_hover', label: 'Table row hover', defaultValue: '#f1f5f9' },
      { key: 'color_input_bg', label: 'Input background', defaultValue: '#ffffff' },
    ],
  },
  {
    title: 'Text colours',
    fields: [
      { key: 'color_text_primary', label: 'Text primary', defaultValue: '#0f172a' },
      { key: 'color_text_secondary', label: 'Text secondary', defaultValue: '#475569' },
      { key: 'color_text_muted', label: 'Text muted', defaultValue: '#94a3b8' },
      { key: 'color_subheader_text', label: 'Subheader text', defaultValue: '#0f172a' },
      { key: 'color_nav_text', label: 'Nav text', defaultValue: '#b3c0d1' },
      { key: 'color_nav_text_muted', label: 'Nav text muted', defaultValue: '#64748b' },
      { key: 'color_nav_active_text', label: 'Nav active text', defaultValue: '#e2e8f0' },
    ],
  },
  {
    title: 'Status colours',
    description: 'Coloured badges for booking, payment and collection statuses.',
    fields: [
      { key: 'color_status_pending_bg', label: 'Pending bg', defaultValue: '#fef3c7' },
      { key: 'color_status_pending_text', label: 'Pending text', defaultValue: '#92400e' },
      { key: 'color_status_confirmed_bg', label: 'In progress bg', defaultValue: '#dbeafe' },
      { key: 'color_status_confirmed_text', label: 'In progress text', defaultValue: '#1e40af' },
      { key: 'color_status_completed_bg', label: 'Completed bg', defaultValue: '#dcfce7' },
      { key: 'color_status_completed_text', label: 'Completed text', defaultValue: '#166534' },
      { key: 'color_status_cancelled_bg', label: 'Cancelled bg', defaultValue: '#f3f4f6' },
      { key: 'color_status_cancelled_text', label: 'Cancelled text', defaultValue: '#6b7280' },
      { key: 'color_status_paid_bg', label: 'Paid bg', defaultValue: '#d1fae5' },
      { key: 'color_status_paid_text', label: 'Paid text', defaultValue: '#065f46' },
      { key: 'color_status_unpaid_bg', label: 'Unpaid bg', defaultValue: '#fee2e2' },
      { key: 'color_status_unpaid_text', label: 'Unpaid text', defaultValue: '#991b1b' },
      { key: 'color_status_collected_bg', label: 'Collected bg', defaultValue: '#d1fae5' },
      { key: 'color_status_collected_text', label: 'Collected text', defaultValue: '#065f46' },
    ],
  },
  {
    title: 'Buttons',
    description: 'Primary, secondary and danger button colour stops.',
    fields: [
      { key: 'color_btn_primary_bg', label: 'Primary bg', defaultValue: '#0f172a' },
      { key: 'color_btn_primary_text', label: 'Primary text', defaultValue: '#ffffff' },
      { key: 'color_btn_primary_hover', label: 'Primary hover', defaultValue: '#1e293b' },
      { key: 'color_btn_secondary_bg', label: 'Secondary bg', defaultValue: '#f8fafc' },
      { key: 'color_btn_secondary_text', label: 'Secondary text', defaultValue: '#0f172a' },
      { key: 'color_btn_danger_bg', label: 'Danger bg', defaultValue: '#ef4444' },
      { key: 'color_btn_danger_text', label: 'Danger text', defaultValue: '#ffffff' },
    ],
  },
  {
    title: 'Border + shadow',
    description: 'Borders for cards, tables, inputs, and nav.',
    fields: [
      { key: 'color_border', label: 'Default border', defaultValue: '#e2e8f0' },
      { key: 'color_card_border', label: 'Card border', defaultValue: '#e2e8f0' },
      { key: 'color_table_border', label: 'Table border', defaultValue: '#e2e8f0' },
      { key: 'color_input_border', label: 'Input border', defaultValue: '#cbd5e1' },
      { key: 'color_input_border_focus', label: 'Input focus', defaultValue: '#2563eb' },
      { key: 'color_subheader_border', label: 'Subheader border', defaultValue: '#e2e8f0' },
      { key: 'color_nav_border', label: 'Nav border', defaultValue: '#1e293b' },
    ],
  },
];

export const ALL_THEME_FIELDS: ThemeField[] = THEME_GROUPS.flatMap((g) => g.fields);

export const DEFAULTS: Record<string, string> = ALL_THEME_FIELDS.reduce(
  (acc, f) => {
    acc[f.key] = f.defaultValue;
    return acc;
  },
  {} as Record<string, string>,
);

export function injectThemePreview(theme: Record<string, string>): void {
  if (typeof document === 'undefined') return;
  let tag = document.getElementById('tricivo-theme') as HTMLStyleElement | null;
  if (!tag) {
    tag = document.createElement('style');
    tag.id = 'tricivo-theme';
    document.head.appendChild(tag);
  }
  const lines = [':root {'];
  for (const [k, v] of Object.entries(theme)) {
    if (!k.startsWith('color_')) continue;
    if (typeof v !== 'string' || !v) continue;
    const cssVar = `--${k.replace(/_/g, '-')}`;
    lines.push(`  ${cssVar}: ${v};`);
  }
  lines.push('}');
  tag.textContent = lines.join('\n');
}
